/**
 * Theme presets for the customer storefront / cart / checkout.
 *
 * Each preset is a complete look (HSL color tokens + light/dark mode +
 * accent hue). Admins pick a preset on `/admin/theme`; the chosen preset
 * is stored on `tenant.branding.theme_preset` and applied at boot via
 * `TenantContext → applyTheme()`.
 */

export const FONT_CHOICES = [
    { label: "Inter", value: "Inter" },
    { label: "Poppins", value: "Poppins" },
    { label: "DM Sans", value: "DM Sans" },
    { label: "Playfair Display", value: "Playfair Display" },
];

// Each preset overrides a small set of CSS variables on :root.
// We keep tokens minimal (primary / accent / background / foreground /
// muted / card / border / radius) so that existing components inherit
// the new look without any markup edits.
export const THEME_PRESETS = {
    "bizil-green": {
        label: "Bizil Green (default)",
        mode: "light",
        defaultBrowseMode: "SHOPS",   // Swiggy-style — restaurant list
        swatch: ["#10B981", "#FFFFFF", "#0A0A0A"],
        tokens: {
            "--background": "60 30% 98%",
            "--foreground": "240 10% 4%",
            "--card": "0 0% 100%",
            "--card-foreground": "240 10% 4%",
            "--primary": "142 71% 38%",
            "--primary-foreground": "0 0% 100%",
            "--accent": "39 100% 50%",
            "--accent-foreground": "0 0% 100%",
            "--muted": "60 8% 95%",
            "--muted-foreground": "240 4% 46%",
            "--border": "60 6% 90%",
            "--destructive": "0 84% 60%",
        },
    },
    "swiggy-orange": {
        label: "Swiggy Orange",
        mode: "light",
        defaultBrowseMode: "SHOPS",
        swatch: ["#FC8019", "#FFFFFF", "#1C1C1C"],
        tokens: {
            "--background": "0 0% 100%",
            "--foreground": "0 0% 11%",
            "--card": "0 0% 100%",
            "--card-foreground": "0 0% 11%",
            "--primary": "23 97% 54%",
            "--primary-foreground": "0 0% 100%",
            "--accent": "10 90% 52%",
            "--accent-foreground": "0 0% 100%",
            "--muted": "0 0% 96%",
            "--muted-foreground": "0 0% 38%",
            "--border": "0 0% 90%",
            "--destructive": "0 84% 60%",
        },
    },
    "zomato-red": {
        label: "Zomato Red",
        mode: "light",
        defaultBrowseMode: "SHOPS",
        swatch: ["#E23744", "#FFFFFF", "#1C1C1C"],
        tokens: {
            "--background": "0 0% 100%",
            "--foreground": "0 0% 9%",
            "--card": "0 0% 100%",
            "--card-foreground": "0 0% 9%",
            "--primary": "355 73% 55%",
            "--primary-foreground": "0 0% 100%",
            "--accent": "20 95% 56%",
            "--accent-foreground": "0 0% 100%",
            "--muted": "0 0% 96%",
            "--muted-foreground": "0 0% 38%",
            "--border": "0 0% 90%",
            "--destructive": "0 84% 60%",
        },
    },
    "midnight-dark": {
        label: "Midnight (Dark)",
        mode: "dark",
        defaultBrowseMode: "SHOPS",
        swatch: ["#7C5CFF", "#0B0B14", "#FFFFFF"],
        tokens: {
            "--background": "240 18% 7%",
            "--foreground": "0 0% 98%",
            "--card": "240 14% 11%",
            "--card-foreground": "0 0% 98%",
            "--primary": "253 100% 68%",
            "--primary-foreground": "0 0% 100%",
            "--accent": "176 80% 55%",
            "--accent-foreground": "0 0% 100%",
            "--muted": "240 12% 16%",
            "--muted-foreground": "240 8% 78%",
            "--border": "240 10% 22%",
            "--destructive": "0 72% 60%",
            "--input": "240 10% 20%",
            "--popover": "240 14% 11%",
            "--popover-foreground": "0 0% 98%",
        },
    },
    // iter55 — 3 storefront-only "marketplace" themes that swap both
    // the colour palette AND the home page layout to match the iconic
    // shape of well-known e-commerce / quick-commerce apps. The
    // ``homeLayout`` discriminator is read by ``HomePage.jsx`` to pick
    // a layout component; if it's absent we fall back to the default
    // home page. Colours are inspired-by, not direct copies — safe
    // for store-listing review.
    "amazon-like": {
        label: "Marketplace — Amazon-style",
        mode: "light",
        homeLayout: "amazon",
        defaultBrowseMode: "PRODUCTS",  // Amazon-style — products first
        swatch: ["#FF9900", "#131A22", "#FFFFFF"],
        tokens: {
            "--background": "0 0% 96%",            // soft grey page bg
            "--foreground": "210 30% 11%",          // navy text
            "--card": "0 0% 100%",
            "--card-foreground": "210 30% 11%",
            "--primary": "33 100% 50%",             // Amazon orange CTAs
            "--primary-foreground": "210 30% 11%",
            "--accent": "210 30% 18%",              // navy nav strip
            "--accent-foreground": "0 0% 100%",
            "--muted": "0 0% 92%",
            "--muted-foreground": "210 10% 38%",
            "--border": "0 0% 85%",
            "--destructive": "0 84% 50%",
        },
    },
    "flipkart-like": {
        label: "Marketplace — Flipkart-style",
        mode: "light",
        homeLayout: "flipkart",
        defaultBrowseMode: "PRODUCTS",  // Flipkart-style — products first
        swatch: ["#2874F0", "#FFE500", "#FFFFFF"],
        tokens: {
            "--background": "0 0% 96%",
            "--foreground": "220 16% 13%",
            "--card": "0 0% 100%",
            "--card-foreground": "220 16% 13%",
            "--primary": "216 87% 55%",             // Flipkart blue
            "--primary-foreground": "0 0% 100%",
            "--accent": "53 100% 50%",              // yellow flash-sale ribbons
            "--accent-foreground": "220 16% 13%",
            "--muted": "0 0% 94%",
            "--muted-foreground": "220 8% 38%",
            "--border": "0 0% 88%",
            "--destructive": "0 84% 55%",
        },
    },
    "blinkit-like": {
        label: "Quick-commerce — Blinkit-style",
        mode: "light",
        homeLayout: "blinkit",
        defaultBrowseMode: "PRODUCTS",  // Blinkit-style — products first
        // Black / white / blue — a Blinkit-inspired quick-commerce look adapted to our brand, not Blinkit's own
        // yellow/green. The homepage layout itself lives in components/BlinkitHome.jsx.
        swatch: ["#2563EB", "#0F172A", "#FFFFFF"],
        tokens: {
            "--background": "220 33% 97%",          // cool off-white page bg
            "--foreground": "222 47% 11%",          // near-black text
            "--card": "0 0% 100%",
            "--card-foreground": "222 47% 11%",
            "--primary": "221 83% 53%",             // blue header / CTAs
            "--primary-foreground": "0 0% 100%",
            "--accent": "222 47% 11%",              // black badges / strong chips
            "--accent-foreground": "0 0% 100%",
            "--muted": "220 25% 94%",
            "--muted-foreground": "220 9% 44%",
            "--border": "220 20% 90%",
            "--destructive": "0 84% 55%",
        },
    },
};

/**
 * iter83 — Resolve the effective browse mode for a customer-facing
 * navigation, following the precedence:
 *
 *   1. ``tileMode`` (explicit per-tile override) when set and not AUTO.
 *   2. ``tenantMode`` (tenant-wide branding default) when set and not AUTO.
 *   3. The theme preset's ``defaultBrowseMode`` (Swiggy/Zomato = SHOPS,
 *      Amazon/Flipkart/Blinkit = PRODUCTS).
 *   4. Final fallback: "SHOPS" — the only mode every tenant has
 *      historical data for.
 *
 * Returns ``"SHOPS"`` or ``"PRODUCTS"`` — never ``"AUTO"``.
 */
export function resolveBrowseMode({ tileMode, tenantMode, presetId } = {}) {
    const norm = (v) => (v ? String(v).toUpperCase() : "");
    const tile = norm(tileMode);
    if (tile === "SHOPS" || tile === "PRODUCTS") return tile;
    const tenant = norm(tenantMode);
    if (tenant === "SHOPS" || tenant === "PRODUCTS") return tenant;
    const preset = THEME_PRESETS[presetId];
    if (preset?.defaultBrowseMode) return preset.defaultBrowseMode;
    return "SHOPS";
}

/** Convert a HEX colour ("#7C5CFF") to the "H S% L%" string our CSS tokens use. */
export function hexToHslTriple(hex) {
    if (!hex) return null;
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
    if (!m) return null;
    const r = parseInt(m[1], 16) / 255;
    const g = parseInt(m[2], 16) / 255;
    const b = parseInt(m[3], 16) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = 0, s = 0;
    const l = (max + min) / 2;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            default: h = (r - g) / d + 4;
        }
        h *= 60;
    }
    return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/** Inject a Google Font <link> tag at most once per family. */
function ensureFontLoaded(family) {
    if (!family) return;
    const id = `gf-${family.replace(/\s+/g, "-")}`;
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family)}:wght@400;500;600;700;800&display=swap`;
    document.head.appendChild(link);
}

/**
 * Apply a theme preset + optional font choice to :root.
 * - Sets every token from the preset
 * - Toggles ``data-theme-mode`` so dark-only CSS can react
 * - Loads the Google Font dynamically and writes ``--font-sans`` / ``--font-heading``
 */
export function applyThemePreset(presetId, opts = {}) {
    const preset = THEME_PRESETS[presetId] || THEME_PRESETS["bizil-green"];
    const root = document.documentElement;
    Object.entries(preset.tokens).forEach(([k, v]) => root.style.setProperty(k, v));
    root.dataset.themeMode = preset.mode;
    if (preset.mode === "dark") {
        root.classList.add("dark");
        // Hint to UA-rendered widgets (scrollbars, inputs, date pickers) to flip.
        root.style.colorScheme = "dark";
    } else {
        root.classList.remove("dark");
        root.style.colorScheme = "light";
    }

    // Optional custom primary HEX overlay (layered on top of preset).
    if (opts.custom_primary_hex) {
        const triple = hexToHslTriple(opts.custom_primary_hex);
        if (triple) {
            root.style.setProperty("--primary", triple);
            root.style.setProperty("--ring", triple);
        }
    } else {
        // Clear ring override so it follows the preset's primary.
        root.style.setProperty("--ring", preset.tokens["--primary"]);
    }

    const body = opts.body_font || "Inter";
    const head = opts.heading_font || body;
    ensureFontLoaded(body);
    if (head !== body) ensureFontLoaded(head);
    root.style.setProperty("--font-sans", `"${body}", system-ui, sans-serif`);
    root.style.setProperty("--font-heading", `"${head}", system-ui, sans-serif`);
    // Also override the existing variables used in index.css so font swap is instant.
    root.style.setProperty("--font-body", `"${body}", system-ui, sans-serif`);
    root.style.setProperty("--font-display", `"${head}", system-ui, sans-serif`);
}
